import {
  GetDailyTanya,
  WeeklyParsha,
  GetLatestClasses,
  GetPopularClasses,
  GetDataFromURL,
  showRecentlyPlayed,
  GetAudianceOptions,
  UserSubscribe,
} from "../../service/service";
import { getPositionSeconds } from "./positionSeconds";
import { fetchFeedItemsOnly } from "./latestFeed";

/*
 * Typed API layer for the redesign. Every endpoint the redesigned pages
 * consume goes through here, so a backend change is a one-file edit and
 * anything not yet built server-side ships behind the same interface with
 * mock data (tracked in BACKEND-TODO.md at the repo root).
 *
 * Real Laravel endpoints wrap the existing service.js functions unchanged.
 */

/** @returns {Promise<{hebrew: string, civil: string}|null>} today's date pair */
export async function getToday() {
  try {
    const res = await GetDailyTanya({ name: "Tanya" });
    const d = res?.date;
    if (!d) return null;
    const civil = new Date(d.gy, (d.gm || 1) - 1, d.gd || 1).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    return {
      hebrew: `${d.hd} ${d.hm} ${d.hy}`,
      hebrewDayMonth: `${d.hd} ${d.hm}`,
      civil,
    };
  } catch (_) {
    return null;
  }
}

/** @returns {Promise<{name: string, names: string[]}|null>} this week's parsha */
export async function getParsha() {
  try {
    const res = await WeeklyParsha({});
    const list = res?.parshas?.length ? res.parshas : res?.data ? [res.data] : [];
    const names = list
      .map((p) => (p?.name || "").replace(/^Parshas\s+/i, ""))
      .filter(Boolean);
    return names.length ? { name: names.join("-"), names } : null;
  } catch (_) {
    return null;
  }
}

const NEW_WINDOW_DAYS = 14;

/**
 * @returns {Promise<Array<{id, title, slug, mediaKind, src, duration, dateLabel, isNew}>>}
 */
export async function getLatestClasses(limit = 3) {
  try {
    // Legacy endpoint supplies the playable src; the redesign feed (when
    // deployed) contributes the container pill (category/series + link).
    const [res, feedItems] = await Promise.all([
      GetLatestClasses(),
      fetchFeedItemsOnly().catch(() => null),
    ]);
    const feedById = new Map((feedItems || []).map((f) => [f.id, f]));
    const rows = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
    return rows
      .map((c) => {
        const media = (c.block || []).find(
          (b) => b.url && ["audio", "video"].includes((b.type || "").toLowerCase())
        );
        if (!media) return null;
        const published = c.is_publish_date || c.created_at;
        const publishedDate = published ? new Date(published) : null;
        return {
          id: c.id,
          title: c.title,
          slug: c.custom_permalink || c.slug,
          mediaKind: (media.type || "audio").toLowerCase(),
          src: media.url,
          duration: media.duration || null,
          // Enrichment from the redesign feed (null-safe until it deploys):
          // true media availability ('both' when audio AND video exist),
          // duration, and the container pill {title, slug, type}.
          media: feedById.get(c.id)?.media || (media.type || "audio").toLowerCase(),
          durationSeconds: feedById.get(c.id)?.durationSeconds || null,
          category: feedById.get(c.id)?.category || null,

          dateLabel: publishedDate
            ? publishedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
            : "",
          isNew: publishedDate
            ? (Date.now() - publishedDate.getTime()) / 86400000 <= NEW_WINDOW_DAYS
            : false,
        };
      })
      .filter(Boolean)
      .slice(0, limit);
  } catch (_) {
    return [];
  }
}

/**
 * Most-viewed classes over the last 24h (user/popular-classes, from
 * soft_analytics). That endpoint only carries legacy blocks, which for the
 * older classes that trend hold no media — so each row is resolved through
 * url-base for its playable src, media flags, and duration. Same item
 * shape as getLatestClasses so the homepage rows are interchangeable.
 */
export async function getPopularClasses(limit = 3) {
  try {
    const res = await GetPopularClasses();
    const rows = Array.isArray(res?.data) ? res.data : [];
    const resolved = await Promise.all(
      rows.slice(0, limit).map((c) => resolvePermalink(c.custom_permalink || c.slug).catch(() => null))
    );
    return resolved
      .filter((r) => r?.kind === "class" && (r.audio || r.video))
      .map((r) => ({
        id: r.id,
        title: r.title,
        slug: r.slug,
        mediaKind: r.audio ? "audio" : "video",
        src: r.audio?.src || r.audio?.streamUrl || r.video?.src,
        media: r.audio && r.video ? "both" : r.video ? "video" : "audio",
        durationSeconds: Number(r.audio?.durationSeconds) > 0 ? Number(r.audio.durationSeconds) : null,
        category: null,
        dateLabel: r.dateLabel,
        isNew: false,
      }));
  } catch (_) {
    return [];
  }
}

/** @returns {Promise<Array<{id, title, slug}>>} Q&A classes from the qa category */
export async function getQaHighlights(limit = 4) {
  try {
    const res = await GetDataFromURL({ node: "qa", encrypted_node: "" });
    const list = res?.data?.list || [];
    return list
      .filter((item) => item.type === "class")
      .slice(0, limit)
      .map((item) => ({
        id: item.id,
        title: item.title || item.name,
        slug: item.custom_permalink || item.slug,
      }));
  } catch (_) {
    return [];
  }
}

/**
 * Signed-in continue data. Preferred source: the Phase 6 progress service
 * (cross-device positions with durations and media URLs). Fallback until it
 * deploys: recent plays joined with device-local resume positions.
 * @returns {Promise<Array<{id, classId, title, slug, src, positionSeconds, durationSeconds}>>}
 */
export async function getContinueItems(limit = 4) {
  if (!localStorage.getItem("accessToken")) return [];

  const { fetchPositions } = await import("./progressSync");
  const serverRows = await fetchPositions();
  if (serverRows && serverRows.length > 0) {
    return serverRows.slice(0, limit).map((row) => {
      const src = row.audio_src || row.audio_stream_url;
      const local = getPositionSeconds(src);
      return {
        id: row.class_id,
        classId: row.class_id,
        title: row.title,
        slug: row.slug,
        src,
        streamUrl: row.audio_stream_url || null,
        positionSeconds: Math.max(row.position_seconds || 0, local),
        durationSeconds: row.duration_seconds || 0,
      };
    });
  }

  try {
    const res = await showRecentlyPlayed();
    const rows = Array.isArray(res?.data) ? res.data : [];
    const seen = new Set();
    const items = [];
    for (const row of rows) {
      if (!row?.url || seen.has(row.url)) continue;
      seen.add(row.url);
      items.push({
        id: row.id,
        classId: row.c_id,
        title: row.title || "Untitled class",
        slug: null,
        src: row.url,
        positionSeconds: getPositionSeconds(row.url),
        durationSeconds: 0,
      });
      if (items.length >= limit) break;
    }
    return items;
  } catch (_) {
    return [];
  }
}

/**
 * Resolve an archive permalink (class / category / page) via user/url-base.
 * For classes, normalizes both block generations into one shape:
 * { kind: 'class', id, title, slug, dateLabel, hebrewDate, tags[],
 *   paragraphs[{text, html}], audio{src,streamUrl,durationSeconds}|null,
 *   video{src,streamUrl,poster}|null, files[{url, downloadUrl, label}] }
 * Non-class results pass through as { kind: type, raw }.
 */
export async function resolvePermalink(node) {
  // ordered:1 → category children come back as ONE curated interleaved list
  // (categories/series/classes by backend sort field) with class duration +
  // media. Backends without the flag return the legacy shape (subcategories
  // first, no media fields) — the normalizer tolerates both.
  const res = await GetDataFromURL({ node, encrypted_node: "", ordered: 1 });
  if (!res?.status || !res?.type) return { kind: "not-found" };
  if (res.type === "category") return normalizeCategory(res.data);
  if (res.type !== "class") return { kind: res.type, raw: res.data };
  return normalizeClassRow(res.data || {});
}

/**
 * Normalize one raw class row (url-base / daily-tanya shape) into the
 * classData contract ClassPage + PlayerCard consume.
 */
export function normalizeClassRow(c) {
  const paragraphs = [];
  let audio = null;
  let video = null;
  const files = [];

  const normalized = Array.isArray(c.normalized_block) ? c.normalized_block : [];
  if (normalized.length > 0) {
    for (const nb of normalized) {
      const b = nb?.block || {};
      switch (nb.block_type) {
        case "paragraph":
          if (b.paragraph_text || b.paragraph_html)
            paragraphs.push({ text: b.paragraph_text || "", html: b.paragraph_html || "" });
          break;
        case "audio":
          if (!audio && (b.audio_src || b.audio_stream_url))
            audio = {
              src: b.audio_src || null,
              streamUrl: b.audio_stream_url || null,
              durationSeconds: b.duration || null,
            };
          break;
        case "video":
          if (!video && (b.video_src || b.video_stream_url))
            video = {
              src: b.video_src || null,
              streamUrl: b.video_stream_url || null,
              poster: b.video_poster || null,
            };
          break;
        case "file":
          if (b.file_url || b.download_url)
            files.push({
              url: b.file_url || b.download_url,
              downloadUrl: b.download_url || b.file_url,
              label: b.file_label || b.download_label || "Source text",
            });
          break;
        default:
          break;
      }
    }
  } else {
    for (const b of c.block || []) {
      const type = (b.type || "").toLowerCase();
      if (type === "paragraph" && b.description) paragraphs.push({ text: b.description, html: "" });
      if (type === "audio" && b.url && !audio) audio = { src: b.url, streamUrl: null, durationSeconds: null };
      if (type === "video" && b.url && !video) video = { src: b.url, streamUrl: null, poster: null };
      if ((type === "documents" || type === "file") && b.url)
        files.push({ url: b.url, downloadUrl: b.url, label: b.description || "Source text" });
    }
  }

  const published = c.is_publish_date || c.created_at;
  return {
    kind: "class",
    id: c.id,
    title: c.title,
    slug: c.custom_permalink || c.slug,
    hebrewDate: c.hebrew_date || null,
    dateLabel: published
      ? new Date(published).toLocaleDateString("en-US", { month: "long", year: "numeric" })
      : "",
    tags: (c.tags || []).map((t) => ({ id: t.id, name: t.name, slug: t.slug })),
    paragraphs,
    audio,
    video,
    files,
  };
}

/**
 * Today's daily shiurim (Tanya or Hayom Yom) as ready-to-render classData
 * rows, plus the date pair for the page heading.
 * @param {"Tanya"|"Hayom Yom"} name
 * @returns {Promise<{date: {hebrew, civil}|null, classes: Array}|null>}
 */
export async function getDailyShiur(name) {
  try {
    const res = await GetDailyTanya({ name });
    if (!res?.status) return null;
    const rows = Array.isArray(res.data) ? res.data : [];
    const d = res.date;
    const date = d
      ? {
          hebrew: `${d.hd} ${d.hm} ${d.hy}`,
          civil: new Date(d.gy, (d.gm || 1) - 1, d.gd || 1).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }),
        }
      : null;
    return { date, classes: rows.map(normalizeClassRow) };
  } catch (_) {
    return null;
  }
}

/**
 * This week's Torah portion(s) with their archive slugs — full objects,
 * unlike getParsha() which only builds the display name.
 * @returns {Promise<Array<{id, name, slug, description}>>}
 */
export async function getParshaPortions() {
  try {
    const res = await WeeklyParsha({});
    const list = res?.parshas?.length ? res.parshas : res?.data ? [res.data] : [];
    return list
      .filter((p) => p && p.slug)
      .map((p) => ({
        id: p.id,
        name: p.title || p.name,
        slug: p.slug,
        description: (p.description || "").replace(/<[^>]+>/g, "").trim(),
      }));
  } catch (_) {
    return [];
  }
}

/**
 * Normalize a url-base category payload for the redesigned category page.
 * `list` arrives with subcategories first (their slug is the FULL nested
 * path, built by the backend from the requested node), then series/classes
 * in taxonomy order. Category children keep that nested slug — swapping in
 * custom_permalink would break /parent/child URL continuity; series and
 * class children prefer custom_permalink like every other surface.
 */
// Date-only strings ("2018-03-01") parse as UTC midnight, which renders as
// the previous day (and for the 1st, the previous MONTH) in US timezones —
// anchor them to local time before formatting.
function parseLocalDate(value) {
  if (!value) return null;
  const s = String(value);
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s);
}

function normalizeCategory(data) {
  const cat = data || {};
  const children = (cat.list || []).map((c, i) => ({
    id: c.id,
    type: c.type, // 'category' | 'series' | 'class'
    title: c.title || c.name,
    slug:
      c.type !== "category" && c.custom_permalink && c.custom_permalink !== "None"
        ? c.custom_permalink
        : c.slug,
    dateISO: c.is_publish_date || null,
    dateLabel: c.is_publish_date
      ? parseLocalDate(c.is_publish_date).toLocaleDateString("en-US", { month: "long", year: "numeric" })
      : "",
    // Array index — the backend already delivered curated order ("Default order").
    order: i,
    durationSeconds: Number(c.duration) > 0 ? Number(c.duration) : null,
    media: c.media || null,
  }));
  return {
    kind: "category",
    id: cat.id,
    title: cat.title || cat.name,
    // The category's OWN unique name (unprefixed) — what /catalog/counts expects.
    slug: cat.custom_permalink && cat.custom_permalink !== "None" ? cat.custom_permalink : cat.slug,
    description: (cat.description || "").replace(/<[^>]+>/g, "").trim(),
    // Editorial flag: class-only leaf renders the course treatment
    // (progress hero, numbered rows) instead of the plain list.
    displayAsCourse: Boolean(cat.display_as_course),
    children,
    raw: cat,
  };
}

/**
 * Fetch a series and its ordered children via url-base {type:'series'}.
 * Children carry order + publish date but no duration/media flags yet
 * (BACKEND-TODO: extend the series payload).
 * @returns {Promise<{title, slug, description, children: Array<{id,type,title,slug,dateLabel,order}>}|null>}
 */
export async function getSeries(slug) {
  const res = await GetDataFromURL({ type: "series", node: slug, encrypted_node: "" });
  if (!res?.status || res.type !== "series") return null;
  const s = res.data || {};
  const children = (s.list || [])
    .map((c) => ({
      id: c.id,
      type: c.type,
      title: c.title || c.name,
      slug: c.custom_permalink && c.custom_permalink !== "None" ? c.custom_permalink : c.slug,
      dateLabel: c.is_publish_date
        ? new Date(c.is_publish_date).toLocaleDateString("en-US", { month: "short", year: "2-digit" }).replace(",", " '")
        : "",
      order: Number(c.order_sequence) || 0,
    }))
    .sort((a, b) => a.order - b.order || String(a.title).localeCompare(String(b.title)));
  return {
    id: s.id,
    title: s.title || s.name,
    slug: s.perma_link && s.perma_link !== "None" ? s.perma_link : s.slug,
    description: (s.description || "").replace(/<[^>]+>/g, "").trim(),
    children,
  };
}

/**
 * Deep tree counts — lights up when the Phase 4 Laravel endpoint
 * (GET /catalog/counts?node=) deploys; returns null until then.
 * @returns {Promise<{classCount: number, seriesCount: number, durationSeconds: number}|null>}
 */
export async function getCatalogCounts(node) {
  try {
    const base = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";
    const res = await fetch(`${base}/catalog/counts?node=${encodeURIComponent(node)}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.status ? json.data : null;
  } catch (_) {
    return null;
  }
}

/**
 * Category ancestry per class (GET /catalog/class-sections?ids=) — every
 * category slug each class sits under, nearest first. {} until the
 * endpoint deploys; callers fall back to the direct category pill.
 * @returns {Promise<Object<string, string[]>>}
 */
export async function getClassSections(ids) {
  const list = [...new Set((ids || []).filter(Boolean))];
  if (list.length === 0) return {};
  try {
    const base = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";
    const res = await fetch(`${base}/catalog/class-sections?ids=${list.join(",")}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return {};
    const json = await res.json();
    return json?.status && json.data && typeof json.data === "object" ? json.data : {};
  } catch (_) {
    return {};
  }
}

/* ------------------------------------------------------------------ */
/* MOCKED — no backend yet. See BACKEND-TODO.md.                       */
/* ------------------------------------------------------------------ */

const BANNERS = "/redesign/banners";
const API_BASE = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";

/** Countdown chip for a promotion: TODAY, or in N days/weeks/months. */
export function countdownChip(isToday, daysUntil) {
  if (isToday) return { kind: "today", label: "TODAY" };
  if (daysUntil <= 1) return { kind: "countdown", label: "tomorrow" };
  if (daysUntil < 14) return { kind: "countdown", label: `in ${daysUntil} days` };
  if (daysUntil < 60) return { kind: "countdown", label: `in ${Math.round(daysUntil / 7)} weeks` };
  return { kind: "countdown", label: `in ${Math.round(daysUntil / 30)} months` };
}

/**
 * Coming Up rail. Preferred source: the Phase 7 calendar engine
 * (GET /calendar/upcoming, materialized nightly from Hebrew-date rules).
 * Fallback until it deploys: the static promotional set below (real
 * dates/hooks from the handoff content samples).
 * @returns {Promise<Array<{key, title, hook, chip: {kind, label}, image?, lockupHebrew?, featured?, href}>>}
 */
export async function getUpcoming() {
  try {
    const res = await fetch(`${API_BASE}/calendar/upcoming?limit=8`, { headers: { Accept: "application/json" } });
    if (res.ok) {
      const json = await res.json();
      if (json?.status && Array.isArray(json.data) && json.data.length > 0) {
        // Only the nearest major date gets the double-width featured card.
        const firstMajor = json.data.findIndex((r) => r.is_major);
        return json.data.map((row, i) => {
          const featured = i === firstMajor;
          return {
            key: `${row.name}-${row.civil_date}`,
            title: row.name,
            hook: row.hook || "",
            chip: countdownChip(row.is_today, row.days_until),
            image: row.image_url || undefined,
            lockupHebrew: row.image_url ? undefined : row.hebrew_label || row.name,
            featured,
            href: row.destination_url || "/jewish-and-chassidic-calendar",
          };
        });
      }
    }
  } catch (_) {
    /* fall through to the static set */
  }
  return [
    {
      key: "19-kislev",
      title: "19 Kislev",
      hook: "Rosh Hashana of Chassidus",
      chip: { kind: "countdown", label: "in 5 months" },
      image: `${BANNERS}/19-kislev.gif`,
      featured: true,
      href: "/jewish-and-chassidic-calendar",
    },
    {
      key: "12-13-tamuz",
      title: "12–13 Tamuz",
      hook: "Chag HaGeulah of the Frierdiker Rebbe",
      chip: { kind: "today", label: "TODAY" },
      image: `${BANNERS}/12-13-tamuz.gif`,
      href: "/jewish-and-chassidic-calendar",
    },
    {
      key: "17-tamuz",
      title: "17 Tamuz",
      hook: "Start of the Three Weeks",
      chip: { kind: "countdown", label: "in 3 days" },
      lockupHebrew: "י״ז תמוז",
      href: "/jewish-and-chassidic-calendar",
    },
    {
      key: "15-av",
      title: "15 Menachem Av",
      hook: "The day ascent begins again",
      chip: { kind: "countdown", label: "in 2 weeks" },
      lockupHebrew: "ט״ו באב",
      href: "/jewish-and-chassidic-calendar",
    },
    {
      key: "chai-elul",
      title: "Chai Elul",
      hook: "Birthdays of the Baal Shem Tov and the Alter Rebbe",
      chip: { kind: "countdown", label: "in 8 weeks" },
      image: `${BANNERS}/18-elul.gif`,
      href: "/jewish-and-chassidic-calendar",
    },
  ];
}

/**
 * Live status. Preferred source: the Phase 7 GET /livestream/status
 * (Castr webhook heuristics + class-schedule starting-soon). Fallback
 * until it deploys: off-air.
 * @returns {Promise<{state: 'live'|'soon'|'offair', label?: string}>}
 */
export async function getLiveStatus() {
  try {
    const res = await fetch(`${API_BASE}/livestream/status`, { headers: { Accept: "application/json" } });
    if (res.ok) {
      const json = await res.json();
      if (json?.status && json.data?.state) {
        return {
          state: json.data.state,
          label: json.data.next?.title
            ? `${json.data.next.title} starting in ~${Math.max(1, Math.round((new Date(json.data.next.starts_at) - Date.now()) / 60000))} min`
            : undefined,
        };
      }
    }
  } catch (_) {
    /* fall through */
  }
  return { state: "offair" };
}

/* ------------------------------------------------------------------ */

/** Newsletter signup via the existing Mailchimp flow. */
export async function subscribeNewsletter(email) {
  const audiences = await GetAudianceOptions();
  const ids = (audiences?.data || audiences?.lists || [])
    .map((a) => a.id)
    .filter(Boolean);
  if (!ids.length) throw new Error("No mailing list available right now.");
  return UserSubscribe({ email, id: [ids[0]] });
}
